import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import HoleMap, { type MapControls } from './components/HoleMap';
import { EMPTY_COURSE, loadCourse } from './lib/courses';
import {
  loadDirectory,
  nearestCourses,
  searchDirectory,
  formatKm,
  type Directory,
  type SearchHit,
} from './lib/directory';
import { useSettings } from './lib/settings';
import type { Course, LngLat } from './lib/types';
import type { MapMode } from './lib/basemap';
import { BRAND } from './lib/brand';

/**
 * GoCaddy for the desktop.
 *
 * Shaped after Apple Maps on macOS on purpose: a translucent sidebar that owns
 * search and navigation, the map filling everything else, controls floating at
 * the top right. That is the layout every Mac user already knows, and a map
 * product that invents its own desktop chrome spends the user's first minute
 * teaching instead of showing.
 *
 * It reuses the SAME HoleMap as the buggy screen and the car screen. The whole
 * point of the one-map work is that a new surface is a new SHELL, not a new
 * map — this file is layout and a course picker, and the map came free.
 *
 * Installable without any native code: open it in Safari and File → Add to
 * Dock. macOS gives it its own icon, window and Dock entry. An Electron or
 * Tauri wrapper buys a ~150 MB download and a second build pipeline, for a
 * product that is a map over a network anyway.
 */
/**
 * Map-mode tiles. Drawn as SVG rather than shipped as screenshots: a thumbnail
 * of a real map goes stale the moment the styling changes, and four PNGs is
 * four more things to keep in sync with what the map actually looks like.
 */
const MODES: { id: MapMode; label: string; thumb: ReactNode }[] = [
  {
    id: 'poly', label: 'Golf',
    thumb: (
      <svg viewBox="0 0 60 60"><rect width="60" height="60" fill="#14241a" />
        <path d="M22 54 Q18 30 30 8 Q40 30 38 54 Z" fill="#2f6b45" />
        <circle cx="31" cy="14" r="4" fill="#4ade80" />
        <ellipse cx="21" cy="35" rx="4" ry="3" fill="#c8b273" />
      </svg>),
  },
  {
    id: 'satellite', label: 'Satellite',
    thumb: (
      <svg viewBox="0 0 60 60"><rect width="60" height="60" fill="#31402c" />
        <path d="M0 40 Q18 26 34 34 T60 28 V60 H0 Z" fill="#46603a" />
        <path d="M20 52 Q26 34 36 12" stroke="#8a9b6e" strokeWidth="3" fill="none" />
        <circle cx="12" cy="16" r="6" fill="#2b3a44" />
      </svg>),
  },
  {
    id: 'hybrid', label: 'Hybrid',
    thumb: (
      <svg viewBox="0 0 60 60"><rect width="60" height="60" fill="#31402c" />
        <path d="M0 40 Q18 26 34 34 T60 28 V60 H0 Z" fill="#46603a" />
        <path d="M6 30 H54 M30 4 V56" stroke="#dfe6ea" strokeWidth="2" opacity=".75" />
        <rect x="18" y="20" width="15" height="7" rx="2" fill="#0b0b0c" opacity=".6" />
      </svg>),
  },
  {
    id: 'road', label: 'Road',
    thumb: (
      <svg viewBox="0 0 60 60"><rect width="60" height="60" fill="#1b212a" />
        <path d="M0 22 H60 M0 42 H60 M22 0 V60" stroke="#3c4654" strokeWidth="4" />
        <path d="M40 0 Q44 30 34 60" stroke="#5a6675" strokeWidth="3" fill="none" />
      </svg>),
  },
];

export default function DesktopApp() {
  const { settings, update } = useSettings();
  const [dir, setDir] = useState<Directory | null>(null);
  const [query, setQuery] = useState('');
  const [course, setCourse] = useState<Course>(EMPTY_COURSE);
  const [loading, setLoading] = useState(false);
  const [holeIndex, setHoleIndex] = useState(0);
  const [fix, setFix] = useState<LngLat | null>(null);
  const [controls, setControls] = useState<MapControls | null>(null);
  const [bearing, setBearing] = useState(0);
  const [modesOpen, setModesOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [follow, setFollow] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = settings.theme;
    document.body.classList.add('desktop-host');
    return () => document.body.classList.remove('desktop-host');
  }, [settings.theme]);

  useEffect(() => {
    void loadDirectory().then(setDir);
    navigator.geolocation?.getCurrentPosition(
      (p) => setFix([p.coords.longitude, p.coords.latitude]),
      () => {},
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, []);

  // Cmd-F / "/" to search, because on a desktop the keyboard is the fast path
  // and a map app that only accepts clicks feels like a website.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey && e.key === 'f') || (e.key === '/' && document.activeElement !== searchRef.current)) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
      if (e.key === 'Escape') {
        searchRef.current?.blur(); setModesOpen(false); setAcctOpen(false); setSettingsOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results: SearchHit[] = useMemo(() => {
    if (!dir) return [];
    if (query.trim().length >= 2) return searchDirectory(dir, { query, near: fix }).slice(0, 40);
    if (fix) return nearestCourses(dir, fix, 12);
    return [];
  }, [dir, query, fix]);

  const pick = (hit: SearchHit) => {
    setLoading(true);
    setHoleIndex(0);
    update({ courseId: hit.id, courseName: hit.name });
    void loadCourse(hit.id).then(({ course: c }) => {
      setCourse(c);
      setLoading(false);
    });
  };

  const hole = course.holes.length ? course.holes[Math.min(holeIndex, course.holes.length - 1)] : null;

  return (
    <div className="dk">
      <aside className="dk-side">
        <div className="dk-brand">{BRAND.name}</div>

        <div className="dk-search">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses"
            spellCheck={false}
          />
        </div>

        {course.holes.length > 0 && (
          <>
            <div className="dk-label">{course.name}</div>
            <div className="dk-holes">
              {course.holes.map((h, i) => (
                <button
                  key={h.number}
                  className={`dk-hole ${i === holeIndex ? 'on' : ''}`}
                  onClick={() => setHoleIndex(i)}
                >
                  <b>{h.number}</b>
                  <span>Par {h.par}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="dk-label">
          {query.trim().length >= 2 ? 'Results' : fix ? 'Nearest' : 'Courses'}
        </div>
        <div className="dk-list">
          {!dir && <div className="dk-empty">Loading 1,449 courses…</div>}
          {dir && !results.length && (
            <div className="dk-empty">
              {query ? 'No course matches that.' : 'Search for a course above.'}
            </div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              className={`dk-row ${r.id === settings.courseId ? 'on' : ''}`}
              onClick={() => pick(r)}
            >
              <span className="dk-row-name">{r.name}</span>
              <span className="dk-row-sub">
                {r.state}
                {r.distanceM != null ? ` · ${formatKm(r.distanceM)}` : ''}
              </span>
            </button>
          ))}
        </div>


      </aside>

      <main className="dk-map">
        <HoleMap
          lockToHole={false}
          cartPaths={null}
          parked={null}
          course={course}
          hole={hole}
          teeColour={settings.teeColour}
          position={fix}
          heading={0}
          aim={null}
          onAimChange={() => {}}
          route={null}
          showHazards
          showCartPaths={settings.cartPathMode}
          follow={false}
          fleet={[]}
          showFleet={false}
          playerUp={false}
          mapMode={settings.mapMode}
          theme={settings.theme}
          onControls={setControls}
          onBearing={setBearing}
          indicators={settings.indicators}
        />

        <div className="dk-ctl" onPointerDown={(e) => e.stopPropagation()}>
          <button
            className={`dk-btn dk-solo dk-avatar ${acctOpen ? 'on' : ''}`}
            onClick={() => { setAcctOpen((v) => !v); setModesOpen(false); }}
            title="Account"
            aria-label="Account"
          >
            <span className="dk-avatar-mark">{BRAND.name.slice(0, 1)}</span>
          </button>

          <button
            className={`dk-btn dk-solo ${modesOpen ? 'on' : ''}`}
            onClick={() => { setModesOpen((v) => !v); setAcctOpen(false); }}
            title="Map modes"
            aria-label="Map modes"
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M9 4L3 6.5v13L9 17l6 3 6-2.5v-13L15 7 9 4z" /><path d="M9 4v13M15 7v13" />
            </svg>
          </button>

          <div className="dk-group">
            <button
              className={`dk-btn ${follow ? 'on' : ''}`}
              onClick={() => { setFollow(true); if (fix) controls?.panBy(0, 0); }}
              title="Centre on me"
              aria-label="Centre on me"
              disabled={!fix}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M21 3L3 10.5l7.5 3L13.5 21 21 3z" />
              </svg>
            </button>
          </div>

          <div className="dk-group">
            <button className="dk-btn" onClick={() => controls?.zoomIn()} title="Zoom in" aria-label="Zoom in">
              <svg viewBox="0 0 24 24" width="19" height="19" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            </button>
            <div className="dk-div" />
            <button className="dk-btn" onClick={() => controls?.zoomOut()} title="Zoom out" aria-label="Zoom out">
              <svg viewBox="0 0 24 24" width="19" height="19" stroke="currentColor" strokeWidth="2"><path d="M5 12h14" /></svg>
            </button>
          </div>

          <button className="dk-btn dk-solo dk-compass" title={`Bearing ${Math.round(bearing)}°`} aria-label="Compass">
            <svg viewBox="0 0 40 40" width="38" height="38" style={{ transform: `rotate(${-bearing}deg)` }}>
              <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" strokeOpacity=".25" />
              <polygon points="20,4 17,11 23,11" fill="#ff4d4d" />
              <text x="20" y="26" textAnchor="middle" fontSize="13" fontWeight="700" fill="currentColor">N</text>
            </svg>
          </button>
        </div>

        {acctOpen && (
          <>
            <div className="dk-scrim" onClick={() => { setAcctOpen(false); setSettingsOpen(false); }} />
            <div className="dk-acct" role="dialog" aria-label="Account">
              <div className="dk-acct-mark">{BRAND.name.slice(0, 1)}</div>
              <div className="dk-acct-name">{settings.buggyId || BRAND.name}</div>
              <div className="dk-acct-sub">
                {settings.role === 'shop' ? 'Pro shop' : 'Cart'} · build {BRAND.build}
              </div>

              <div className="dk-acct-rows">
                <button
                  className="dk-acct-row"
                  onClick={() => {
                    /* Prefills the build id and the browser string. A support mail
                       without them costs one round trip every single time. */
                    const body = encodeURIComponent(
                      `\n\n---\nBuild: ${BRAND.build}\nCourse: ${course.name || 'none'}\n` +
                      `Map: ${settings.mapMode}\nScreen: ${innerWidth}x${innerHeight}\n${navigator.userAgent}`
                    );
                    location.href = `mailto:?subject=${encodeURIComponent(BRAND.name + ' report')}&body=${body}`;
                  }}
                >
                  <span className="dk-acct-ico dk-ico-report">!</span>
                  <span>Report a problem</span>
                  <span className="dk-acct-chev">›</span>
                </button>
                <div className="dk-acct-div" />
                <button className="dk-acct-row" onClick={() => setSettingsOpen((v) => !v)}>
                  <span className="dk-acct-ico dk-ico-settings">⚙</span>
                  <span>Settings</span>
                  <span className="dk-acct-chev">{settingsOpen ? '⌄' : '›'}</span>
                </button>
              </div>

              {settingsOpen && (
                <div className="dk-set">
                  <label>
                    <span>Theme</span>
                    <select
                      value={settings.theme}
                      onChange={(e) => update({ theme: e.target.value as 'dark' | 'light' })}
                    >
                      <option value="dark">Dark</option>
                      <option value="light">Light</option>
                    </select>
                  </label>
                  <label>
                    <span>Distances</span>
                    <select
                      value={settings.unit}
                      onChange={(e) => update({ unit: e.target.value as 'm' | 'yd' })}
                    >
                      <option value="m">Metres</option>
                      <option value="yd">Yards</option>
                    </select>
                  </label>
                  <label>
                    <span>Cart paths</span>
                    <select
                      value={settings.cartPathMode ? 'on' : 'off'}
                      onChange={(e) => update({ cartPathMode: e.target.value === 'on' })}
                    >
                      <option value="on">Shown</option>
                      <option value="off">Hidden</option>
                    </select>
                  </label>
                </div>
              )}
            </div>
          </>
        )}

        {modesOpen && (
          <>
            <div className="dk-scrim" onClick={() => setModesOpen(false)} />
            <div className="dk-modes" role="dialog" aria-label="Map modes">
              <div className="dk-modes-title">Map Modes</div>
              <div className="dk-modes-grid">
                {MODES.map((m) => (
                  <button
                    key={m.id}
                    className={`dk-mode ${settings.mapMode === m.id ? 'on' : ''}`}
                    onClick={() => { update({ mapMode: m.id }); setModesOpen(false); }}
                  >
                    <span className="dk-mode-thumb">{m.thumb}</span>
                    <span className="dk-mode-name">{m.label}</span>
                  </button>
                ))}
              </div>
              <div className="dk-modes-foot">
                © OpenStreetMap contributors · Imagery © Esri
              </div>
            </div>
          </>
        )}
        {hole && (
          <div className="dk-badge">
            <b>Hole {hole.number}</b>
            <span>Par {hole.par}</span>
          </div>
        )}
        {loading && <div className="dk-badge dk-loading">Building the course…</div>}
      </main>
    </div>
  );
}
